/**
 * Edit agent_config (Phase 6) - the base agent instructions, the confidence-escalation
 * threshold, and the morning-email send time. See worker/src/compliance/rules.ts for how
 * the first two flow into the compliance prompt, and worker/src/admin/cron.ts for how the
 * send time is converted to the underlying UTC cron expression.
 */
import { useEffect, useState } from 'react';
import { fetchAgentConfig, fetchCronSettings } from '../../admin/queries';
import { updateAgentConfig, updateMorningSummarySchedule, ApiError } from '../../api';
import { Banner, Button, Tile } from '../../components/ui';

const LOCAL_TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function AgentConfigAdmin() {
  const [baseInstructions, setBaseInstructions] = useState('');
  const [threshold, setThreshold] = useState(0.6);
  const [localTime, setLocalTime] = useState('07:00');
  const [currentLabel, setCurrentLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savedPrompt, setSavedPrompt] = useState(false);
  const [savedSchedule, setSavedSchedule] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [prompt, cron] = await Promise.all([fetchAgentConfig('compliance_prompt'), fetchCronSettings()]);
        const value = prompt?.value ?? {};
        if (typeof value['base_instructions'] === 'string') setBaseInstructions(value['base_instructions']);
        if (typeof value['confidence_escalation_threshold'] === 'number') setThreshold(value['confidence_escalation_threshold']);
        const label = cron.find((c) => c.key === 'morning_summary_local_time_label')?.value ?? '';
        setCurrentLabel(label);
        const match = /^(\d{2}:\d{2})/.exec(label);
        if (match) setLocalTime(match[1]!);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load the agent config.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function savePrompt() {
    if (!baseInstructions.trim()) {
      setError('Base instructions cannot be empty.');
      return;
    }
    if (threshold < 0 || threshold > 1) {
      setError('The confidence threshold must be between 0 and 1.');
      return;
    }
    setSavingPrompt(true);
    setError(null);
    setSavedPrompt(false);
    try {
      await updateAgentConfig('compliance_prompt', { base_instructions: baseInstructions, confidence_escalation_threshold: threshold });
      setSavedPrompt(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the agent config.');
    } finally {
      setSavingPrompt(false);
    }
  }

  async function saveSchedule() {
    if (!LOCAL_TIME_RE.test(localTime)) {
      setError('Enter the send time as HH:MM, 24-hour (e.g. 07:00).');
      return;
    }
    setSavingSchedule(true);
    setError(null);
    setSavedSchedule(false);
    try {
      const result = await updateMorningSummarySchedule(localTime);
      setCurrentLabel(result.label);
      setSavedSchedule(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the send time.');
    } finally {
      setSavingSchedule(false);
    }
  }

  if (loading) return <Tile>Loading…</Tile>;

  return (
    <div className="stack">
      {error ? <Banner tone="error">{error}</Banner> : null}

      <Tile className="admin-card">
        <h3 className="checklist-cat__title">Base agent instructions</h3>
        <p className="muted">Prepended to every compliance / risk / errors pass system prompt.</p>
        <textarea
          className="input note-textarea"
          rows={8}
          value={baseInstructions}
          onChange={(e) => {
            setBaseInstructions(e.target.value);
            setSavedPrompt(false);
          }}
        />
        <label className="admin-row__toggle" style={{ marginTop: 'var(--space-3)' }}>
          Confidence escalation threshold (0–1)
          <input
            className="input admin-row__number"
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={threshold}
            onChange={(e) => {
              setThreshold(Number(e.target.value));
              setSavedPrompt(false);
            }}
          />
        </label>
        {savedPrompt ? <Banner tone="good">Saved.</Banner> : null}
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Button onClick={() => void savePrompt()} disabled={savingPrompt}>
            {savingPrompt ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Tile>

      <Tile className="admin-card">
        <h3 className="checklist-cat__title">Morning email send time</h3>
        <p className="muted">Currently: {currentLabel || 'not set'}</p>
        <label className="admin-row__toggle">
          Send time (24-hour, local)
          <input
            className="input"
            style={{ maxWidth: 120 }}
            value={localTime}
            onChange={(e) => {
              setLocalTime(e.target.value);
              setSavedSchedule(false);
            }}
            placeholder="07:00"
          />
        </label>
        {savedSchedule ? <Banner tone="good">Saved.</Banner> : null}
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Button onClick={() => void saveSchedule()} disabled={savingSchedule}>
            {savingSchedule ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Tile>
    </div>
  );
}
